import type { PublicEvent } from './publicEvent'

export type EventDates = {
  day: string
  month: string
  when: string
  days: number
}

const MS_PER_DAY = 24 * 60 * 60 * 1000
const RANGE_DASH = '\u2009–\u2009'

export function eventDates(
  event: Pick<PublicEvent, 'starts_at' | 'ends_at' | 'time_zone'>,
  locale: string
): EventDates | null {
  if (!event.starts_at) return null

  const start = new Date(event.starts_at)
  const end = event.ends_at ? new Date(event.ends_at) : null
  const format = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { timeZone: event.time_zone, ...options })
  const date = format({ weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  const time = format({ hour: 'numeric', minute: '2-digit' })
  const dayOf = format({ day: 'numeric' })
  const monthOf = format({ month: 'short' })

  if (!end)
    return {
      day: dayOf.format(start),
      month: monthOf.format(start),
      when: `${date.format(start)} · ${time.format(start)}`,
      days: 1,
    }

  const last = new Date(Math.max(start.getTime(), end.getTime() - 1))
  const isoDate = new Intl.DateTimeFormat('en-CA', { timeZone: event.time_zone })
  const days = (Date.parse(isoDate.format(last)) - Date.parse(isoDate.format(start))) / MS_PER_DAY + 1

  if (days === 1) {
    const times =
      isoDate.format(start) === isoDate.format(end)
        ? time.formatRange(start, end)
        : `${time.format(start)}${RANGE_DASH}${time.format(end)}`
    return { day: dayOf.format(start), month: monthOf.format(start), when: `${date.format(start)} · ${times}`, days }
  }

  const startMonth = monthOf.format(start)
  const endMonth = monthOf.format(last)
  return {
    day: `${dayOf.format(start)}${RANGE_DASH}${dayOf.format(last)}`,
    month: startMonth === endMonth ? startMonth : `${startMonth}${RANGE_DASH}${endMonth}`,
    when: format({ weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).formatRange(
      start,
      end
    ),
    days,
  }
}
